import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { ArrowRight, ArrowDropDown } from '@material-ui/icons';
import type { ICellRendererParams } from 'ag-grid-community';

/** Context shape provided by `MasterDetailGrid` for expand/collapse cell renderers. */
export interface MasterDetailContext {
  isExpanded: (rowId: string) => boolean;
  onExpandToggle: (rowId: string) => void;
  expanded?: { has: (groupId: string) => boolean };
  toggle?: (groupId: string) => void;
  canShowDetailRow?: (data: unknown) => boolean;
  /** Left padding so detail panels align with `detailPanelAnchorField` on the master grid. */
  detailPanelLeftOffset?: number;
}

const EXPAND_LABELS = {
  expand: 'Expand row',
  collapse: 'Collapse row',
};

interface ExpandCellTheme {
  spacing?: (value: number) => number | string;
  shape?: { borderRadius?: number };
  transitions?: {
    create?: (props: string[], options?: { duration?: number }) => string;
    duration?: { short?: number };
  };
  gridTextColor?: string;
}

const expandCellStyles = (theme: ExpandCellTheme) => {
  const spacing = (value: number) =>
    (typeof theme.spacing === 'function' ? theme.spacing(value) : `${value * 8}px`);
  return {
    expandButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      border: 'none',
      font: 'inherit',
      padding: `${spacing(0.5)} ${spacing(1)}`,
      borderRadius: theme.shape?.borderRadius || 4,
      transition: theme.transitions?.create
        ? theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration?.short,
          })
        : 'background-color 0.2s ease, color 0.2s ease',
      width: '100%',
      height: '100%',
      cursor: 'pointer',
      color: theme.gridTextColor,
      '& svg': {
        fontSize: '26px',
        width: '26px',
        height: '26px',
        transition: 'transform 0.2s ease',
      },
      '&:active svg': {
        transform: 'scale(0.95)',
      },
    },
  };
};

const preventEventPropagation = (event: React.SyntheticEvent) => {
  event.preventDefault();
  event.stopPropagation();
  const nativeEvent = event.nativeEvent as Event & { stopImmediatePropagation?: () => void };
  nativeEvent.stopImmediatePropagation?.();
};

interface ExpandCellRow {
  _isDetail?: boolean;
  _masterRowId?: string;
  id?: string;
}

interface ExpandCellProps extends ICellRendererParams {
  classes: Record<string, string>;
  context: MasterDetailContext;
}

function ExpandCell({ classes, data, context }: ExpandCellProps) {
  const row = data as ExpandCellRow | undefined;
  if (!row || row._isDetail || !context) {
    return null;
  }
  if (context.canShowDetailRow && !context.canShowDetailRow(row)) {
    return null;
  }
  const rowId = row._masterRowId ?? row.id ?? '';
  const isExpanded = context.expanded?.has?.(rowId) ?? context.isExpanded?.(rowId) ?? false;
  const toggle = (event: React.MouseEvent) => {
    preventEventPropagation(event);
    if (context.toggle) {
      context.toggle(rowId);
    } else {
      context.onExpandToggle?.(rowId);
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      onMouseDown={preventEventPropagation}
      onPointerDown={preventEventPropagation}
      className={classes.expandButton}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? EXPAND_LABELS.collapse : EXPAND_LABELS.expand}
    >
      {isExpanded ? <ArrowDropDown /> : <ArrowRight />}
    </button>
  );
}

/**
 * Expand/collapse control for a `MasterDetailGrid` consumer column. Set it as the
 * `cellRenderer` of the column you pass to `expandColumnId`.
 */
export const ExpandCellRenderer = withStyles(
  expandCellStyles as unknown as Parameters<typeof withStyles>[0]
)(ExpandCell);

export default ExpandCellRenderer;
